var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { createRef, Fragment, useCallback, useEffect, useRef, } from 'react';
import { flip, offset, shift, useFloating, } from '@floating-ui/react-dom';
import { Popover as HeadlessPopover, Portal, Transition, } from '@headlessui/react';
import clsx from 'clsx';
import rafSchd from 'raf-schd';
import { BackgroundProvider } from '../BackgroundContext/index.js';
import { ChevronDownIcon } from '../icons/index.js';
import { useUpdateOnScroll, useFloatingFullContext, FloatingFullContext, } from '../utils/floating/index.js';
import { runIfFn, useControlledState, useGetLatest } from '../utils/index.js';
import { PopoverContext, usePopover, } from './PopoverContext.js';
const DELAY_HIDE = 100;
const DELAY_SHOW = 1000;
export const Popover = (props) => {
    var _a;
    const { children, padding = 'md', placement = 'bottom', trigger = 'click', interactive = false, defaultVisible = false, visible: propsVisible, delayShow = false } = props, rest = __rest(props, ["children", "padding", "placement", "trigger", "interactive", "defaultVisible", "visible", "delayShow"]);
    const [visible, setVisible] = useControlledState({
        initial: defaultVisible,
        value: propsVisible,
    });
    const hideTimer = useRef();
    const showTimer = useRef();
    useEffect(() => () => clearTimeout(hideTimer.current), []);
    useEffect(() => () => clearTimeout(showTimer.current), []);
    const { x, y, reference, floating, strategy, refs, update } = useFloating({
        placement,
        middleware: [offset(8), shift(), flip()],
    });
    useUpdateOnScroll(refs.reference, update, 'scroll');
    useUpdateOnScroll(refs.reference, update, 'resize');
    const getLatest = useGetLatest({
        visible,
        triggerRef: refs.reference,
        tooltipRef: refs.floating,
    });
    const isTriggeredBy = useCallback((t) => {
        return trigger === t;
    }, [trigger]);
    const show = useCallback(() => {
        clearTimeout(hideTimer.current);
        clearTimeout(showTimer.current);
        const delayShowTime = typeof delayShow === 'boolean' ? DELAY_SHOW : delayShow;
        showTimer.current = window.setTimeout(() => setVisible(true), interactive && delayShow ? delayShowTime : 0);
    }, [setVisible]);
    const hide = useCallback(() => {
        clearTimeout(hideTimer.current);
        clearTimeout(showTimer.current);
        hideTimer.current = window.setTimeout(() => setVisible(false), interactive ? DELAY_HIDE : 0);
    }, [interactive, setVisible]);
    const toggle = useCallback(() => {
        if (getLatest().visible) {
            hide();
        }
        else {
            show();
        }
    }, [getLatest, hide, show]);
    useEffect(() => {
        const handleClickOutside = event => {
            var _a, _b, _c, _d;
            const target = ((_b = (_a = event.composedPath) === null || _a === void 0 ? void 0 : _a.call(event)) === null || _b === void 0 ? void 0 : _b[0]) || event.target;
            if (target instanceof Node) {
                if (refs.floating.current != null &&
                    refs.reference.current != null &&
                    !((_c = refs.floating.current) === null || _c === void 0 ? void 0 : _c.contains(target)) &&
                    !((_d = refs.reference.current) === null || _d === void 0 ? void 0 : _d.contains(target))) {
                    hide();
                }
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [hide, refs.reference, refs.floating.current]);
    useEffect(() => {
        var _a;
        if (refs.reference.current == null || !isTriggeredBy('click'))
            return;
        (_a = refs.reference.current) === null || _a === void 0 ? void 0 : _a.addEventListener('click', toggle);
        return () => { var _a; return (_a = refs.reference.current) === null || _a === void 0 ? void 0 : _a.removeEventListener('click', toggle); };
    }, [refs.reference, isTriggeredBy, toggle]);
    useEffect(() => {
        if (refs.reference.current == null || !isTriggeredBy('hover'))
            return;
        refs.reference.current.addEventListener('mouseenter', show);
        refs.reference.current.addEventListener('mouseleave', hide);
        return () => {
            var _a, _b;
            (_a = refs.reference.current) === null || _a === void 0 ? void 0 : _a.removeEventListener('mouseenter', show);
            (_b = refs.reference.current) === null || _b === void 0 ? void 0 : _b.removeEventListener('mouseleave', hide);
        };
    }, [refs.reference, show, hide, isTriggeredBy]);
    useEffect(() => {
        var _a, _b;
        if (refs.floating.current == null || !interactive)
            return;
        (_a = refs.floating.current) === null || _a === void 0 ? void 0 : _a.addEventListener('mouseenter', show);
        (_b = refs.floating.current) === null || _b === void 0 ? void 0 : _b.addEventListener('mouseleave', hide);
        return () => {
            var _a, _b;
            (_a = refs.floating.current) === null || _a === void 0 ? void 0 : _a.removeEventListener('mouseenter', show);
            (_b = refs.floating.current) === null || _b === void 0 ? void 0 : _b.removeEventListener('mouseleave', hide);
        };
    }, [refs.floating.current, show, hide, interactive]);
    const isReferenceHidden = ((_a = refs.reference.current) === null || _a === void 0 ? void 0 : _a.offsetParent) === null;
    useEffect(() => {
        if (isReferenceHidden)
            hide();
    }, [hide, isReferenceHidden]);
    return (React.createElement(HeadlessPopover, Object.assign({}, rest),
        React.createElement(PopoverContext.Provider, { value: {
                open: visible,
                onOpen: show,
                onClose: hide,
                padding,
            } },
            React.createElement(FloatingFullContext.Provider, { value: {
                    strategy,
                    x,
                    y,
                    reference,
                    floating,
                    update,
                } }, children))));
};
const PopoverTrigger = props => {
    const { children, className } = props, rest = __rest(props, ["children", "className"]);
    const { reference } = useFloatingFullContext();
    return (React.createElement(HeadlessPopover.Button, Object.assign({ ref: reference, className: clsx('text-basicSurface-500 hover:text-basicSurface-900', 'group bg-surface-50 rounded-md inline-flex items-center text-base font-medium', 'focus:outline-none focus-visible:ring', className) }, rest), renderProps => (React.createElement(React.Fragment, null,
        React.createElement("span", null, runIfFn(children, renderProps)),
        React.createElement(ChevronDownIcon, { className: clsx(renderProps.open
                ? 'text-basicSurface-600'
                : 'text-basicSurface-400', 'ml-2 h-5 w-5 group-hover:text-basicSurface-500'), "aria-hidden": "true" })))));
};
const PopoverTriggerMinimal = props => {
    const { children } = props, rest = __rest(props, ["children"]);
    const { reference } = useFloatingFullContext();
    return (React.createElement(HeadlessPopover.Button, Object.assign({}, rest, { ref: reference }), renderProps => runIfFn(children, renderProps)));
};
const PopoverPanel = props => {
    const { children, className, rounded = false } = props, rest = __rest(props, ["children", "className", "rounded"]);
    const { open, onClose } = usePopover();
    const { x, y, strategy, floating, update } = useFloatingFullContext();
    const panelRef = createRef();
    const roundedClsx = [!rounded && 'rounded-lg', rounded && 'rounded-full'];
    useEffect(() => {
        const schedule = rafSchd(update);
        if (open) {
            schedule();
        }
        return () => schedule === null || schedule === void 0 ? void 0 : schedule.cancel();
    }, [update, open, children]);
    const style = {
        position: strategy,
        top: '0',
        left: '0',
        transform: `translate(${Math.round(x)}px,${Math.round(y)}px)`,
    };
    return (React.createElement(Portal, null,
        React.createElement("div", { ref: panelRef, style: style },
            React.createElement(BackgroundProvider, { backgroundType: "surface" },
                React.createElement(Transition, { show: open, as: Fragment, enter: "transition ease-out duration-200", enterFrom: "opacity-0 translate-y-1", enterTo: "opacity-100 translate-y-0", leave: "transition ease-in duration-150", leaveFrom: "opacity-100 translate-y-0", leaveTo: "opacity-0 translate-y-1", beforeEnter: () => floating(panelRef.current), afterLeave: () => floating(null) },
                    React.createElement(HeadlessPopover.Panel, Object.assign({ static: true, className: clsx([
                            'w-full max-w-md max-w-[100vw]',
                            'shadow-lg overflow-hidden border border-surface-200 focus-visible:ring focus:outline-none',
                            'bg-surface-50 text-basicSurface-500',
                            x === null && 'invisible',
                            roundedClsx,
                            className,
                        ]) }, rest), () => runIfFn(children, { open, close: onClose })))))));
};
const PopoverContent = props => {
    const { children, className } = props, rest = __rest(props, ["children", "className"]);
    const { padding } = usePopover();
    return (React.createElement("div", Object.assign({ className: clsx([
            'relative',
            padding === 'sm' && 'px-2 py-3 sm:px-3',
            padding === 'md' && 'px-4 py-5 sm:p-6 ',
            padding === 'lg' && 'px-5 py-6 sm:p-8',
            className,
        ]) }, rest), children));
};
const PopoverFooter = props => {
    const { children, className } = props, rest = __rest(props, ["children", "className"]);
    const { padding } = usePopover();
    return (React.createElement("div", Object.assign({ className: clsx([
            'bg-surface-100',
            padding === 'sm' && 'px-2 py-3 sm:px-3',
            padding === 'md' && 'px-4 py-5 sm:p-6 ',
            padding === 'lg' && 'px-5 py-5 sm:px-8',
            className,
        ]) }, rest), children));
};
Popover.Trigger = PopoverTrigger;
Popover.TriggerMinimal = PopoverTriggerMinimal;
Popover.Panel = PopoverPanel;
Popover.Content = PopoverContent;
Popover.Footer = PopoverFooter;
//# sourceMappingURL=Popover.js.map